<template>
  <v-row
    no-gutters
    justify="center"
  >
    <v-col
      v-if="details"
      xl="5"
      md="7"
      class="pt-6"
    >
      <v-alert
        v-if="details.trial && details.trial.active"
        type="info"
        class="text-body-1 mb-6"
      >
        {{ $t('admin.billing.trial_ends') }} <strong>{{ $dayjs(details.trial.trial_end).format('MMM D, YYYY') }}</strong>.
        <span v-if="!details.default_payment_method">{{ $t('admin.billing.add_payment_method', { plan: details.subscription_plan.name }) }}</span>
        <span v-else>{{ $t('admin.billing.charged_automatically') }}</span>
      </v-alert>
      <v-card
        class="pa-0"
        rounded="lg"
      >
        <v-card-title class="d-block grey lighten-5 pa-6">
          <div>
            {{ $t('admin.billing.details') }}
          </div>
        </v-card-title>
        <v-card-text class="pa-8 text-body-1">
          <v-row
            no-gutters
          >
            <v-col
              cols="4"
              class="px-6 pt-2"
            >
              {{ $t('admin.billing.subscription_plan') }}
            </v-col>
            <v-col class="pt-2">
              <div class="font-weight-bold text-body-1 mb-1">
                {{ details.subscription_plan.name }}
                <v-chip
                  v-if="details.trial && details.trial.active"
                  color="info"
                  label
                  dark
                  x-small
                  class="ml-2"
                >
                  {{ $t('admin.billing.trial') }}
                </v-chip>
              </div>
            </v-col>
            <v-col
              v-if="(!details.trial || !details.trial.active) && details.default_payment_method"
              cols="auto"
            >
              <v-btn
                color="primary"
                text
                small
                class="text-none"
                @click="subscriptionPlansDialog.visible = true"
              >
                <v-icon
                  x-small
                  left
                >
                  far fa-pen
                </v-icon>
                {{ $t('admin.billing.change') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-divider class="my-4" />
          <v-row
            no-gutters
            align="center"
          >
            <v-col
              cols="4"
              class="px-6"
            >
              {{ $t('admin.billing.payment_method') }}
            </v-col>
            <v-col>
              <div
                v-if="!details.default_payment_method"
                class="text-body-1 grey--text"
              >
                {{ $t('admin.billing.none') }}
              </div>
              <div v-else>
                <v-row
                  class="grey--text text--darken-3"
                  no-gutters
                  align="center"
                >
                  <v-col cols="auto">
                    <v-icon
                      class="mr-6"
                      color="grey darken-2"
                      style="font-size: 30px"
                    >
                      fal fa-credit-card
                    </v-icon>
                  </v-col>
                  <v-col>
                    <div>
                      <div class="font-weight-bold text-body-1 mb-1">
                        {{ details.default_payment_method.card.brand | uppercase }} ending in {{ details.default_payment_method.card.last4 }}
                      </div>
                      <div class="grey--text text-caption">
                        {{ $t('admin.billing.expires_in') }} {{ details.default_payment_method.card.exp_month }}/{{ details.default_payment_method.card.exp_year }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-col>
            <v-col cols="auto">
              <v-btn
                v-if="!details.default_payment_method"
                color="primary"
                text
                small
                class="text-none"
                @click="initCardDialog()"
              >
                <v-icon
                  x-small
                  left
                >
                  far fa-plus
                </v-icon>
                {{ $t('admin.billing.add_payment') }}
              </v-btn>
              <v-btn
                v-else
                color="primary"
                text
                small
                class="text-none"
                @click="initCardDialog()"
              >
                <v-icon
                  x-small
                  left
                >
                  far fa-pen
                </v-icon>
                {{ $t('admin.billing.change') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-divider class="my-4" />
          <v-row
            no-gutters
          >
            <v-col
              cols="4"
              class="px-6 pt-2"
            >
              {{ $t('admin.billing.invoice_details') }}
            </v-col>
            <v-col class="pt-2">
              <div class="font-weight-bold text-body-1 mb-1">
                {{ details.invoice_details.name }}
              </div>
              <div
                v-if="details.invoice_details.company"
                class="font-weight-bold text-body-1 mb-1"
              >
                {{ details.invoice_details.company }}
              </div>
              <div
                v-if="details.invoice_details.vat_id"
                class="text-body-1 mb-1"
              >
                {{ $t('admin.billing.vat_id') }} {{ details.invoice_details.vat_id }}
              </div>
              <div
                v-if="details.invoice_details.address"
                class="text-body-1 mb-1"
              >
                {{ details.invoice_details.address }}
              </div>
              <div
                v-if="details.invoice_details.post_code"
                class="text-body-1 mb-1"
              >
                {{ details.invoice_details.post_code }}, {{ details.invoice_details.city }}
              </div>
              <div
                v-if="details.invoice_details.country"
                class="text-body-1"
              >
                {{ details.invoice_details.country }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="primary"
                text
                small
                class="text-none"
                @click="invoiceDetailsDialog.visible = true"
              >
                <v-icon
                  x-small
                  left
                >
                  far fa-pen
                </v-icon>
                {{ $t('admin.billing.edit') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-divider class="my-4" />
          <v-row
            no-gutters
          >
            <v-col
              cols="4"
              class="px-6 pt-2"
            >
              {{ $t('admin.billing.email') }}
            </v-col>
            <v-col class="pt-2">
              <div class="text-body-1 mb-1">
                {{ details.invoice_details.email }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="primary"
                text
                small
                class="text-none"
                @click="invoiceDetailsDialog.visible = true"
              >
                <v-icon
                  x-small
                  left
                >
                  far fa-pen
                </v-icon>
                {{ $t('admin.billing.edit') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card
        class="pa-0 mt-8"
        rounded="lg"
      >
        <v-card-title class="d-block grey lighten-5 pa-6">
          <div>
            {{ $t('admin.billing.invoices') }}
          </div>
        </v-card-title>
        <v-card-text class="pa-0">
          <div
            v-if="invoices.data.length === 0"
            class="py-12 grey--text text-center"
          >
            {{ $t('admin.billing.no_invoices') }}
          </div>
          <v-simple-table v-else>
            <tbody>
              <tr
                v-for="invoice in invoices.data"
                :key="invoice.id"
              >
                <td class="pa-6">
                  <div>{{ $dayjs(invoice.service_at).format('MMM YYYY') }}</div>
                  <div class="caption grey--text">
                    {{ invoice.invoice_id }}
                  </div>
                </td>
                <td
                  style="width: 1px"
                  class="text-no-wrap"
                >
                  {{ invoice.currency }} {{ invoice.price_gross | currency('') }}
                </td>
                <td style="width: 1px">
                  <v-btn
                    small
                    color="primary"
                    text
                    class="text-none"
                    :href="invoice.download_url"
                    target="_blank"
                  >
                    <v-icon
                      left
                      x-small
                    >
                      fal fa-download
                    </v-icon>
                    {{ $t('admin.billing.download') }}
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-col>

    <card-dialog
      v-model="cardDialog.visible"
      :title="$t('admin.billing.add_payment_method_title')"
      max-width="500px"
    >
      <v-card-text class="pa-4">
        <form id="payment-form">
          <div id="payment-element">
            <v-row justify="center">
              <v-col
                cols="auto"
                class="py-4"
              >
                <v-progress-circular
                  :size="40"
                  :width="1"
                  color="grey"
                  indeterminate
                />
              </v-col>
            </v-row>

            <!-- Elements will create form elements here -->
          </div>

          <v-alert
            v-if="cardDialog.error"
            class="mt-5"
            type="error"
            outlined
          >
            {{ cardDialog.error }}
          </v-alert>
        </form>
      </v-card-text>

      <template v-slot:actions>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            class="text-none"
            :loading="cardDialog.submitting"
            @click="submitPayment()"
          >
            {{ $t('admin.billing.save') }}
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>

    <card-dialog
      v-if="details"
      v-model="invoiceDetailsDialog.visible"
      :title="$t('admin.billing.invoice_details_title')"
      max-width="500px"
    >
      <v-card-text class="pa-4">
        <validation-observer ref="invoiceDetailsValidationObserver">
          <subtitle class="mb-3">
            {{ $t('admin.billing.email_for_invoices') }}
          </subtitle>

          <validation-provider rules="email">
            <v-text-field
              v-model="details.invoice_details.email"
              :label="$t('admin.billing.email')"
              outlined
            />
          </validation-provider>

          <subtitle class="mb-3">
            {{ $t('admin.billing.invoice_details') }}
          </subtitle>

          <validation-provider rules="required">
            <v-text-field
              v-model="details.invoice_details.name"
              :label="$t('admin.billing.name')"
              outlined
            />
          </validation-provider>

          <validation-provider>
            <v-text-field
              v-model="details.invoice_details.company"
              :label="$t('admin.billing.company_name')"
              outlined
            />
          </validation-provider>

          <validation-provider>
            <v-text-field
              v-model="details.invoice_details.vat_id"
              :label="$t('admin.billing.vat_id_optional')"
              outlined
            />
          </validation-provider>

          <validation-provider rules="required">
            <v-text-field
              v-model="details.invoice_details.address"
              :label="$t('admin.billing.address')"
              outlined
            />
          </validation-provider>

          <v-row>
            <v-col cols="4">
              <validation-provider rules="required">
                <v-text-field
                  v-model="details.invoice_details.post_code"
                  :label="$t('admin.billing.post_code')"
                  outlined
                />
              </validation-provider>
            </v-col>
            <v-col>
              <validation-provider rules="required">
                <v-text-field
                  v-model="details.invoice_details.city"
                  :label="$t('admin.billing.city')"
                  outlined
                />
              </validation-provider>
            </v-col>
          </v-row>

          <validation-provider rules="required">
            <v-text-field
              v-model="details.invoice_details.country"
              :label="$t('admin.billing.country')"
              outlined
            />
          </validation-provider>
        </validation-observer>
      </v-card-text>

      <template v-slot:actions>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            class="text-none"
            :loading="invoiceDetailsDialog.submitting"
            @click="submitInvoiceDetails()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>

    <card-dialog
      v-if="details"
      v-model="subscriptionPlansDialog.visible"
      :title="$t('admin.billing.select_plan')"
      max-width="1000px"
    >
      <v-card-text class="pa-4">
        <v-row dense>
          <v-col
            v-for="plan in details.subscription_plans"
            :key="plan.id + '_sub'"
          >
            <v-card
              outlined
              :elevation="plan.id === details.subscription_plan.id ? 10 : 0"
            >
              <v-card-title>
                <div>
                  <div
                    class="text-subtitle-1 font-weight-bold"
                    :class="{ 'primary--text': plan.id === details.subscription_plan.id }"
                  >
                    {{ plan.name }}
                  </div>
                  <div class="text-body-1 mt-1">
                    {{ plan.description }}
                  </div>
                </div>
              </v-card-title>
              <v-divider />
              <v-card-text class="text-body-1">
                <v-row
                  v-for="(feature, index) in plan.features"
                  :key="plan.id + 'feature' + index"
                  no-gutters
                  align="center"
                  class="mb-2"
                >
                  <v-col cols="1">
                    <v-icon
                      v-if="feature.active"
                      small
                      color="success"
                    >
                      far fa-check
                    </v-icon>
                    <v-icon
                      v-else
                      small
                      color="error"
                    >
                      far fa-times
                    </v-icon>
                  </v-col>
                  <v-col class="pl-2">
                    {{ feature.text }}
                  </v-col>
                </v-row>
              </v-card-text>
              <v-divider />
              <v-btn
                v-if="plan.id === details.subscription_plan.id"
                text
                large
                disabled
                block
                class="text-none"
              >
                {{ $t('admin.billing.current_plan') }}
              </v-btn>
              <v-btn
                v-else-if="subscriptionPlansDialog.newPlan && plan.id === subscriptionPlansDialog.newPlan.id"
                block
                color="primary"
                large
                plain
                class="text-none"
                @click="subscriptionPlansDialog.newPlan = undefined"
              >
                {{ $t('admin.billing.selected') }}
                <v-icon
                  right
                  x-small
                >
                  far fa-check
                </v-icon>
              </v-btn>
              <v-btn
                v-else
                block
                color="primary"
                text
                large
                class="text-none"
                @click="subscriptionPlansDialog.newPlan = plan"
              >
                {{ $t('admin.billing.select_plan_button') }}
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <template v-slot:actions>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="subscriptionPlansDialog.newPlan && subscriptionPlansDialog.newPlan.id !== details.subscription_plan.id"
            color="primary"
            class="text-none"
            :loading="invoiceDetailsDialog.submitting"
            @click="submitInvoiceDetails()"
          >
            {{ subscriptionPlansDialog.newPlan.rank > details.subscription_plan.rank ? $t('admin.billing.confirm_upgrade') : $t('admin.billing.confirm_downgrade') }}
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>
  </v-row>
</template>

<script>
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { OrganisationApiService } from '@/data/services/OrganisationApiService'
import CardDialog from '@/components/helpers/CardDialog.vue'
import Subtitle from '@/components/helpers/Subtitle.vue'

export default {
  name: 'AdminBillingView',
  components: {
    Subtitle,
    CardDialog
  },
  mixins: [deleteConfirmationMixin],
  data: () => ({
    loading: false,
    details: null,
    cardDialog: {
      visible: false,
      submitting: false,
      redirectUrl: null,
      error: null
    },
    invoiceDetailsDialog: {
      visible: false,
      submitting: false
    },
    invoices: {
      data: [],
      loading: false
    },
    subscriptionPlansDialog: {
      visible: false,
      plans: [],
      newPlan: undefined
    }
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    }
  },
  created() {
    this.getSubscriptionData()
    this.getInvoices()
  },
  methods: {
    async getInvoices() {
      this.invoices.loading = true

      try {
        const result = await OrganisationApiService.getInvoices()

        this.invoices.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'There was an error loading billing data',
          color: 'error'
        })
      } finally {
        this.invoices.loading = false
      }
    },
    async getSubscriptionData() {
      this.loading = true

      try {
        this.details = await OrganisationApiService.getSubscriptionDetails()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'There was an error loading billing data',
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    initCardDialog() {
      this.cardDialog.visible = true

      this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PK)

      try {
        const options = {
          clientSecret: this.details.setup_intent.client_secret,
          // Fully customizable with appearance API.
          appearance: { }
        }

        this.elements = this.stripe.elements(options)

        setTimeout(() => {
          const paymentElement = this.elements.create('payment')
          paymentElement.mount('#payment-element')
        }, 500)
      } catch (e) {
        this.cardDialog.error = 'There was an error'

        throw e
      }
    },
    async submitPayment() {
      this.cardDialog.submitting = true

      const elements = this.elements
      const result = await this.stripe.confirmSetup({
        elements,
        redirect: 'if_required'
      })

      this.cardDialog.submitting = false

      if (result.error) {
        this.error = result.error.message
      } else {
        console.log(result)

        await OrganisationApiService.addPaymentMethod({
          payment_method_id: result.setupIntent.payment_method
        })

        this.cardDialog.visible = false

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: 'Payment method successfully saved'
        })

        this.getSubscriptionData()
      }
    },
    async submitInvoiceDetails() {
      if (!(await this.$refs.invoiceDetailsValidationObserver.validate())) return

      this.invoiceDetailsDialog.submitting = true

      try {
        await OrganisationApiService.submitInvoiceDetails(this.details.invoice_details)

        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'success',
          text: 'Invoice details successfully updated'
        })

        this.invoiceDetailsDialog.visible = false
      } catch (e) {
      } finally {
        this.loading = false
      }
    }

  }
}
</script>
